import type { ErrorEvent } from "@sentry/react";
import { httpClientIntegration, init } from "@sentry/react";

import { APP_VERSION } from "@/constants/app.constants";
import { SENTRY_IGNORED_ERRORS } from "@/constants/sentry.constants";

/**
 * Marks HTTP Client integration events (see below) so beforeSend can
 * downgrade their level — server error responses aren't app-crashing
 * exceptions and shouldn't be reported at the default "error" level.
 *
 * Prefix rather than equality: Sentry 11 stamps the transport into the
 * mechanism (`auto.http.client.fetch`, `auto.http.client.xhr`) where 7 used a
 * bare `http.client`. An exact match keeps compiling and keeps passing — it
 * just stops matching, and every failed API response goes back to burying the
 * real crashes at "error" level.
 */
const isHttpClientEvent = (mechanismType: string | undefined): boolean =>
  mechanismType?.startsWith("auto.http.client") === true;

/**
 * Two integrations put a URL on `request.url`, and neither consults
 * `dataCollection` — not even the `urlQueryParams` set below, which only
 * reaches URLs through `filterCollectedUrl` during span processing.
 * `httpClientIntegration` writes the failed request's URL onto its own
 * events, where the query string carries FHIR search params.
 * `httpContextIntegration` — a browser default, kept by the `integrations`
 * spread below — writes `location.href` onto **every** event: a render
 * crash, an unhandled rejection, an explicit `captureException`. There the
 * query string is whatever the route left in the address bar, including the
 * password-reset `secret` in the moment before `SetPasswordPage` scrubs it.
 * This is the only thing stripping either, so beforeSend runs it on every
 * event rather than only on HTTP-client ones.
 */
const redactUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.search = "";
    parsedUrl.hash = "";
    return parsedUrl.toString();
  } catch {
    return url;
  }
};

/**
 * Exported for its test rather than left inline: the redaction is a privacy
 * control, and the level downgrade beside it is keyed off a string the SDK
 * owns, so both need checks that fail when an upgrade moves them.
 */
export const beforeSend = (event: ErrorEvent): ErrorEvent => {
  if (event.request?.url) {
    event.request.url = redactUrl(event.request.url);
  }

  const hasHttpClientMechanism = event.exception?.values?.some(
    (exceptionValue) => isHttpClientEvent(exceptionValue.mechanism?.type)
  );

  if (hasHttpClientMechanism) {
    event.level = "info";
  }

  return event;
};

export const sentry = {
  init: () => {
    const SENTRY_DSN = import.meta.env.VITE_PUBLIC_SENTRY_DSN;

    if (!SENTRY_DSN) {
      return;
    }

    const apiTargets = [
      import.meta.env.VITE_PUBLIC_AGENT_API_BASE_URL,
      import.meta.env.VITE_PUBLIC_MEDPLUM_BASE_URL,
      import.meta.env.VITE_PUBLIC_EHR_SYNC_BASE_URL,
    ].filter(Boolean);

    init({
      dsn: SENTRY_DSN,
      environment: import.meta.env.VITE_ENV || "development",
      release: APP_VERSION,
      attachStacktrace: true,
      ignoreErrors: SENTRY_IGNORED_ERRORS,
      /**
       * v7 gated cookies and the HTTP-client headers behind `sendDefaultPii`,
       * which we never turned on. v11 deleted that option for `dataCollection`
       * and defaults every field to **on**, so the upgrade alone would have
       * started sending cookies, request and response headers, and user
       * fields off a clinical app.
       *
       * `User-Agent` is the one exception, and it is an allow-list rather than
       * a blanket `false` for two reasons: v7's `httpContext` integration put
       * it on every event unconditionally, so dropping it would lose browser
       * and OS context we have always had; and Sentry's own "filter out
       * crawlers and legacy browsers" feature reads it. The allow-list is
       * substring, case-insensitive — `Referer` does not match it, so it is
       * now `[Filtered]`, which v7 did send.
       *
       * Not strictly less than v7, though: an allow-list is not `false`.
       * `_INTERNAL_filterKeyValueData` keeps every header *key* and replaces
       * only the disallowed *values*, so a failed-API event now ships the
       * request's header key set where v7's `_shouldSendDefaultPii()` gate
       * sent no headers at all. No secret values leave the browser — the key
       * set does.
       *
       * `urlQueryParams` is listed for completeness. Nothing reads it today
       * (`filterCollectedUrl` is reached only from span processing and
       * `requestDataIntegration`, neither of which is active without tracing),
       * and the query string that matters is stripped by `redactUrl` above.
       */
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: { request: { allow: ["user-agent"] }, response: false },
        httpBodies: [],
        urlQueryParams: false,
      },
      integrations: (integrations) => [
        ...integrations,
        httpClientIntegration({
          failedRequestStatusCodes: [[400, 599]],
          failedRequestTargets: apiTargets,
        }),
      ],
      beforeSend,
    });
  },
};
